import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import moment, { Moment } from 'moment'
import { v4 as uuid } from 'uuid'
import { Chip, FormGenerator } from '@microservices/wiskey-react-components'
import {
  DataGrid,
  DataGridProProps as MUIDataGridProps,
  useGridApiRef,
} from '@microservices/wiskey-react-table-component'
import { Button, Grid, useTheme } from '@mui/material'
import {
  GridColDef,
  GridColumnHeaderParams,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid'

import { useDisplayFormState } from '@components/DisplayForm/hooks'

import { useFetchObjectByCodeQuery, useFetchObjectFieldChangesMutation } from '@redux/api'

import { ScrollingParams, useInfiniteScroll } from '@hooks'
import { buttonStylesByMode, generalFieldStylesByMode } from '@helpers'
import { FIELD_VALUE_TYPE, GENERATOR_INPUT_TYPE, OBJECT_FIELD_CHANGES_COLUMNS } from '@constants'
import { AutocompleteOption, GETObjectFieldChangesParams, ObjectFieldChangesDTO } from '@types'

import { FormContext } from '../../DisplayForm'
import { ValueFilterRow } from '../ValueFilterRow'

export const ObjectFieldChangesTable: FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()

  const { currentLink, isSuccessUpdateObjectData } = useContext(FormContext)
  const { isFormDialogWindow } = useDisplayFormState()

  const [historyPage, setHistoryPage] = useState(0)

  const defaultFormValues = {
    fields: [] as AutocompleteOption[],
    author: '' as const,
    dateRange: [] as [],
    newValueType: { label: 'Other', id: 'other' } as AutocompleteOption,
    oldValueType: { label: 'Other', id: 'other' } as AutocompleteOption,
    newValue: '' as const,
    oldValue: '' as const,
  }

  const methods = useForm<{
    fields: AutocompleteOption[]
    author: ''
    dateRange: []
    newValueType: AutocompleteOption
    oldValueType: AutocompleteOption
    newValue: ''
    oldValue: ''
  }>({
    defaultValues: defaultFormValues,
  })

  const {
    watch,
    reset,
    formState: { isDirty },
    setValue,
  } = methods

  const apiRef = useGridApiRef()

  const watchField = watch('fields')
  const watchAuthor = watch('author')
  const watchDateRange = watch('dateRange')
  const watchNewValueType = watch('newValueType')
  const watchNewValue = watch('newValue')
  const watchOldValueType = watch('oldValueType')
  const watchOldValue = watch('oldValue')

  const {
    combinedData: fieldChanges,
    readMore,
    refresh,
    currentSort,
    isLoading: areFieldChangesFetching,
    totalPages,
  } = useInfiniteScroll<GETObjectFieldChangesParams, ObjectFieldChangesDTO>(
    useFetchObjectFieldChangesMutation,
    {
      objectCode: currentLink.objectCode,
      objectId: currentLink.objectId,
    }
  )

  const { data: objectModel } = useFetchObjectByCodeQuery(currentLink.objectCode, {
    skip: !currentLink.objectCode,
  })

  useEffect(() => {
    if (isSuccessUpdateObjectData) {
      setHistoryPage(0)
      handleRefreshFieldChanges()
    }
  }, [isSuccessUpdateObjectData])

  const fieldOptions = useMemo(() => {
    if (!objectModel) return []

    return objectModel.fields.map(field => {
      return { label: field.name, id: field.name }
    })
  }, [objectModel?.fields])

  const columnsList: GridColDef<ObjectFieldChangesDTO>[] = OBJECT_FIELD_CHANGES_COLUMNS.map(
    column => ({
      ...column,
      renderCell: params => {
        if (column.field === 'field') {
          return (
            <Chip
              label={params.value}
              variant='outlined'
              sx={{
                height: isFormDialogWindow ? '19px' : '23px',
              }}
            />
          )
        }

        return <>{params.value}</>
      },
    })
  )

  const [columns, setColumns] = useState(columnsList)

  const handleAutosizeEvent = (params: GridColumnHeaderParams<GridValidRowModel, any, any>) => {
    apiRef.current.autosizeColumns({
      columns: [params.field],
      includeOutliers: true,
    })
  }

  useEffect(() => {
    if (!apiRef.current) return

    const { scrollbarSize, rowWidth: tableWidth } =
      apiRef.current.getRootDimensions && apiRef.current.getRootDimensions()

    setColumns(prev => {
      const updatedColumns = prev.map(col => ({
        ...col,
        width: (tableWidth - scrollbarSize) / prev.length,
      }))

      return updatedColumns
    })

    const unsubscribe = apiRef.current.subscribeEvent(
      'columnHeaderDoubleClick',
      handleAutosizeEvent
    )

    return () => {
      unsubscribe()
    }
  }, [apiRef])

  // TODO Костыль. Нужно переходить на курсорную пагинацию
  const allHistoryChanges = useMemo(
    () =>
      fieldChanges.map(field => ({
        ...field,
        id: uuid(),
      })),
    [fieldChanges]
  )

  const processedHistoryChanges = allHistoryChanges.map(record => {
    const isDateField = objectModel
      ? objectModel.fields.some(field => {
          return record.field === field.name && field.valueType === FIELD_VALUE_TYPE.DATETIME
        })
      : false

    return {
      ...record,
      changeDate: moment(record.changeDate).format('DD.MM.YYYY HH:mm:ss'),
      newValue:
        isDateField && record.newValue !== 'null'
          ? moment(record.newValue).format('DD.MM.YYYY HH:mm:ss')
          : record.newValue,
      oldValue:
        isDateField && record.oldValue !== 'null'
          ? moment(record.oldValue).format('DD.MM.YYYY HH:mm:ss')
          : record.oldValue,
    }
  })

  const handleRefreshFieldChanges = (params?: Omit<ScrollingParams, 'page'>) => {
    refresh(params)

    apiRef.current.scrollToIndexes && apiRef.current.scrollToIndexes({ rowIndex: 0 })
  }

  const handleSetFieldsForFilter = () => {
    const buildContainsFilter = (
      fieldName: string,
      value?: string | (string | Moment | null)[],
      isDateRange = false
    ) => {
      if (!value) return null

      if (isDateRange) {
        const startDate = value[0]
          ? `${fieldName}>="${moment.isMoment(value[0]) ? value[0].toISOString() : value[0]}"`
          : null

        const endDate = value[1]
          ? `${fieldName}<="${moment.isMoment(value[1]) ? value[1].toISOString() : value[1]}"`
          : null

        const filter = [startDate, endDate].filter(Boolean).join(';')

        return filter
      }

      if (typeof value != 'string') return null

      const desiredValue = value.trim() === '*' ? encodeURIComponent('\\*') : `"${value.trim()}*"`

      return `${fieldName}==${desiredValue}`
    }

    const getFilterFields = () => {
      const filterFields = watchField.length
        ? watchField.map(field => `field==${field.label}`).join(' or ')
        : null

      return filterFields ? `(${filterFields})` : null
    }

    const filters = [
      getFilterFields(),
      buildContainsFilter('newValue', watchNewValue),
      buildContainsFilter('oldValue', watchOldValue),
      buildContainsFilter('author', watchAuthor),
      buildContainsFilter('changeDate', watchDateRange, true),
    ]

    const generatedFilter = filters.filter(Boolean).join(';')

    handleRefreshFieldChanges({
      filter: generatedFilter || undefined,
    })
  }

  const debouncedSetFieldsForFilterFn = debounce(handleSetFieldsForFilter, 500)

  const handleHistoryPageChange: MUIDataGridProps['onRowsScrollEnd'] = () => {
    readMore()
  }

  const handleSetHistorySorting = (value: GridSortModel | null) => {
    const isSorting = value && value.length
    handleRefreshFieldChanges({ sort: isSorting ? value : [] })
  }

  const handleReset = () => {
    reset(defaultFormValues)

    handleRefreshFieldChanges({ filter: undefined })
  }

  const handleSearch = () => {
    debouncedSetFieldsForFilterFn()
  }

  const handleColumnWidthChange = () => {
    if (!apiRef || !apiRef.current) return

    setColumns(apiRef.current.getAllColumns())
  }

  const sxInput = {
    '& .MuiOutlinedInput-root': {
      height: '25px',
      backgroundColor: '#fff',
    },
    '& .MuiInputAdornment-root button': {
      padding: '0',
      marginRight: '8px',
    },
  }

  const sxInputLabel = {
    color: 'black',
  }

  return (
    <Grid
      container
      sx={{
        width: '100%',
        px: 1,
        '& .MuiGrid-item:not(:first-child)': {
          paddingTop: '0px',
        },
      }}
    >
      <Grid item xs={12}>
        <FormProvider {...methods}>
          <FormGenerator
            inputs={[
              {
                name: 'fields',
                inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                label: t('label.fields'),
                formInputContainerSx: {
                  marginBottom: '5px',
                  '& .Mui-expanded .Mui-focused': {
                    border: '2px solid #7d7d7d !important',
                  },
                },
                autocompleteOptions: fieldOptions,
                multiple: true,
                sx: {
                  '& + .MuiTypography-root': {
                    color: theme.palette.color.field,
                    border: '2px solid transparent',
                  },
                  '& > .MuiFormControl-root': {
                    ...generalFieldStylesByMode(
                      theme,
                      undefined,
                      undefined,
                      undefined,
                      isFormDialogWindow
                    ),
                  },
                  '& .MuiAutocomplete-tag': {
                    height: '18px',
                  },
                },
              },
              {
                name: 'author',
                inputType: GENERATOR_INPUT_TYPE.INPUT,
                label: t('label.author'),
                labelSx: sxInputLabel,
                sx: sxInput,
                formInputContainerSx: { mb: 0.5 },
              },
              {
                name: 'dateRange',
                inputType: GENERATOR_INPUT_TYPE.DATE_RANGE_PICKER,
                value: watchDateRange,
                label: t('label.dateRange'),
                sx: sxInput,
                labelSx: sxInputLabel,
                formInputContainerSx: { mb: 0.5 },
              },
            ]}
          />

          <ValueFilterRow
            setValue={setValue}
            sxInput={sxInput}
            typeName='newValueType'
            typeValue={watchNewValueType}
            valueName='newValue'
          />

          <ValueFilterRow
            setValue={setValue}
            sxInput={sxInput}
            typeName='oldValueType'
            typeValue={watchOldValueType}
            valueName='oldValue'
          />

          <Grid container item gap={1.5} justifyContent='flex-end' mb={1} mt={0.5} pr={0.5}>
            <Button
              disabled={!isDirty}
              sx={theme => ({
                ...buttonStylesByMode(theme, isFormDialogWindow),
              })}
              onClick={handleReset}
            >
              {t('filter.reset')}
            </Button>

            <Button
              sx={theme => ({
                ...buttonStylesByMode(theme, isFormDialogWindow),
              })}
              onClick={handleSearch}
            >
              {t('filter.search')}
            </Button>
          </Grid>
        </FormProvider>
      </Grid>

      <Grid item xs={12}>
        <DataGrid
          {...(isFormDialogWindow && { columnHeaderHeight: 24 })}
          {...(isFormDialogWindow && { rowHeight: 24 })}
          hideFooter
          apiRef={apiRef}
          columns={columns}
          dataGridBoxSx={{ height: '300px' }}
          disableColumnResize={false}
          fetchNextPage={handleHistoryPageChange}
          loading={areFieldChangesFetching}
          pageCount={totalPages}
          paginationModel={{ page: historyPage, pageSize: 5 }}
          rows={processedHistoryChanges ? processedHistoryChanges : []}
          sortModel={currentSort}
          sortingMode={'server'}
          onColumnWidthChange={handleColumnWidthChange}
          onSortModelChange={handleSetHistorySorting}
        />
      </Grid>
    </Grid>
  )
}
